import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  InputAdornment,
  Drawer,
  Typography,
  Chip,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import GitHubIcon from "@mui/icons-material/GitHub";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useParams } from "react-router-dom";
import { getHistoryLogs } from "../api/tool";

const DetailRow = ({ label, value }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    py={1.5}
    px={2}
    borderBottom="1px solid #333"
  >
    <Typography variant="body2" color="#ccc">
      {label}
    </Typography>
    <Typography variant="body2" color="#fff" fontWeight="500">
      {value}
    </Typography>
  </Box>
);

export default function HistoryPage() {
  const [historyLogs, setHistoryLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [selectedLog, setSelectedLog] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  // Filter states
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedTools, setSelectedTools] = useState([]);
  const [availableTools, setAvailableTools] = useState([]);
  
  const { id } = useParams();

  const handleOpenDrawer = (log) => {
    setSelectedLog(log);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setSelectedLog(null);
    setIsDrawerOpen(false);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleToolFilterChange = (tool) => {
    setSelectedTools(prev => 
      prev.includes(tool) 
        ? prev.filter(t => t !== tool)
        : [...prev, tool]
    );
  };



  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getHistoryLogs(id);
      const logs = Array.isArray(res?.data) ? res.data : [];
      setHistoryLogs(logs);
      
      // Extract unique tools from logs
      const tools = [...new Set(logs.map(log => log.tool))].filter(Boolean);
      setAvailableTools(tools);
      
      // Initialize selected tools with all tools
      if (selectedTools.length === 0) {
        setSelectedTools(tools);
      }
    } catch (err) {
      console.error("Error fetching history logs:", err);
    } finally {
      setLoading(false);
    }
  }, [id, selectedTools.length]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    const sortedLogs = [...historyLogs].sort(
      (a, b) => new Date(b.timeStamp) - new Date(a.timeStamp)
    );

    const filtered = sortedLogs.filter((log) => {
      const matchesSearch = log.action
        .toLowerCase()
        .includes(search.toLowerCase());
      const matchesStatus =
        filterStatus === "ALL" ||
        (filterStatus === "SUCCESS" && log.response.success) ||
        (filterStatus === "FAILURE" && !log.response.success);
      const matchesTool = selectedTools.includes(log.tool);
      
      return matchesSearch && matchesStatus && matchesTool;
    });
    setFilteredLogs(filtered);
  }, [search, filterStatus, historyLogs, selectedTools]);

  const handleRefresh = () => {
    fetchHistory();
    setSearch("");
    setFilterStatus("ALL");
    setSelectedTools(availableTools);
  };

  const isFilterOpen = Boolean(filterAnchorEl);
  const filterId = isFilterOpen ? 'tool-filter-popover' : undefined;

  return (
    <>
      {/* Filters */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1.5}
        flexWrap="nowrap"
        sx={{
          px: 1,
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {/* Left Side: Search + Filter */}
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by action"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#ccc" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 220,
              backgroundColor: "#1e1e1e",
              input: { color: "#fff" },
              fieldset: { borderColor: "#444" },
              "& .MuiInputBase-root": {
                paddingRight: "4px",
              },
            }}
          />

          <TextField
            select
            variant="outlined"
            size="small"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            sx={{
              minWidth: 160,
              backgroundColor: "#1e1e1e",
              fieldset: { borderColor: "#444" },
              "& .MuiInputLabel-root": { color: "#ccc" },
              "& .MuiSvgIcon-root": { color: "#ccc" },
              "& .MuiSelect-select": { color: "#fff" },
            }}
          >
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="SUCCESS">Success</MenuItem>
            <MenuItem value="FAILURE">Failure</MenuItem>
          </TextField>
        </Box>

        {/* Right Side: Filter Icon + Refresh Icon */}
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            onClick={handleFilterClick}
            sx={{
              color: "#ccc",
              position: "relative",
            }}
          >
            <FilterListIcon />
            {selectedTools.length < availableTools.length && (
              <Box
                position="absolute"
                top={6}
                right={6}
                width={8}
                height={8}
                borderRadius="50%"
                backgroundColor="#3b82f6"
              />
            )}
          </IconButton>

          <IconButton
            onClick={handleRefresh}
            sx={{
              color: "#ccc",
              mr: "10px",
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Tool Filter Popover */}
      <Popover
        id={filterId}
        open={isFilterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#1e1e1e",
            color: "#fff",
            border: "1px solid #444",
            minWidth: 200,
            maxWidth: 300,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "#fff", fontWeight: 600 }}>
            Filter by Tools
          </Typography>
          


          <FormGroup>
            {availableTools.map((tool) => (
              <FormControlLabel
                key={tool}
                control={
                  <Checkbox
                    checked={selectedTools.includes(tool)}
                    onChange={() => handleToolFilterChange(tool)}
                    size="small"
                    sx={{
                      color: "#ccc",
                      "&.Mui-checked": {
                        color: "#3b82f6",
                      },
                    }}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" sx={{ color: "#fff" }}>
                      {tool}
                    </Typography>
                  </Box>
                }
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                  },
                }}
              />
            ))}
          </FormGroup>

          {availableTools.length === 0 && (
            <Typography variant="body2" sx={{ color: "#888", textAlign: "center", py: 2 }}>
              No tools available
            </Typography>
          )}

          <Divider sx={{ my: 2, borderColor: "#333" }} />

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" sx={{ color: "#ccc" }}>
              {selectedTools.length} of {availableTools.length} selected
            </Typography>
            <Box display="flex" gap={1}>
              {selectedTools.length < availableTools.length && (
                <Chip
                  label={`${availableTools.length - selectedTools.length} filtered`}
                  size="small"
                  sx={{
                    backgroundColor: "#3b82f6",
                    color: "#fff",
                    fontSize: "0.7rem",
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Popover>

      {/* Table Section */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={3}>
          <CircularProgress size={30} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            background: "#1a1a1a",
            borderRadius: 2,
            overflowX: "auto",
            maxHeight: "calc(100vh - 160px)",
            overflowY: "auto",
            boxShadow: "0 0 10px rgba(0,0,0,0.6)",
          }}
        >
          <Table stickyHeader sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#2a2a2a" }}>
                {[
                  "Date",
                  "Action",
                  "Duration",
                  "Response Code",
                  "Tool",
                  "Success",
                  "Details",
                ].map((head) => (
                  <TableCell
                    key={head}
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      fontSize: "0.95rem",
                      px: 2,
                      py: 1.2,
                      borderBottom: "1px solid #444",
                      backgroundColor: "#2a2a2a",
                    }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.map((log, idx) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{
                    borderBottom: "1px solid #333",
                    "&:last-child td": { borderBottom: 0 },
                  }}
                >
                  <TableCell sx={{ color: "#ccc", px: 2, py: 1.2 }}>
                    {new Date(log.timeStamp).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ color: "#ccc", px: 2, py: 1.2 }}>
                    {log.action}
                  </TableCell>
                  <TableCell sx={{ color: "#ccc", px: 2, py: 1.2 }}>
                    {log.duration}
                  </TableCell>
                  <TableCell sx={{ color: "#ccc", px: 2, py: 1.2 }}>
                    {log.responseCode}
                  </TableCell>
                  <TableCell sx={{ color: "#ccc", px: 2, py: 1.2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {log.tool}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: log.response.success ? "#4caf50" : "#f44336",
                      fontWeight: 600,
                      px: 2,
                      py: 1.2,
                    }}
                  >
                    {log.response.success ? "SUCCESS" : "FAILURE"}
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 1.2 }}>
                    <IconButton
                      onClick={() => handleOpenDrawer(log)}
                      size="small"
                    >
                      <VisibilityIcon sx={{ color: "#ccc" }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredLogs.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{ color: "#888", textAlign: "center", py: 3 }}
                  >
                    No History in this Server
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Drawer for Log Details */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            backgroundColor: "#1e1e1e",
            color: "#fff",
            width: "50%",
            minWidth: 500,
            maxWidth: 700,
          },
        }}
      >
        {selectedLog && (
          <Box sx={{ p: 3, fontFamily: "sans-serif" }}>
            {/* Header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <GitHubIcon sx={{ color: "#fff" }} />
                <Typography variant="h6" component="h2">
                  {selectedLog.tool} {selectedLog.action}
                </Typography>
              </Box>
              {!selectedLog.response.success && (
                <Chip
                  icon={<ErrorOutlineIcon />}
                  label="Error"
                  color="error"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>

            {/* Timeline */}
            <Box position="relative">
              {/* Timeline Line */}
              <Box
                position="absolute"
                left="12px"
                top="12px"
                bottom="12px"
                width="2px"
                backgroundColor="#333"
              />

              {/* Action Invoked */}
              <Box display="flex" gap={2} mb={2} position="relative">
                <Box
                  width={24}
                  height={24}
                  borderRadius="50%"
                  backgroundColor="#3b82f6"
                  sx={{ border: "2px solid #1e1e1e", zIndex: 1 }}
                />
                <Box flex={1}>
                  <Typography variant="body1" fontWeight="bold">
                    Action Invoked
                  </Typography>
                  <Typography variant="body2" color="#ccc">
                    at {new Date(selectedLog.timeStamp).toLocaleString()}
                  </Typography>
                  <Paper
                    sx={{ mt: 1, backgroundColor: "#2a2a2a", borderRadius: 2 }}
                  >
                    <DetailRow label="Log ID" value={selectedLog.logId} />
                    <DetailRow label="User ID" value={selectedLog.userId} />
                    <DetailRow label="Server ID" value={selectedLog.serverId} />
                  </Paper>
                </Box>
              </Box>

              {/* Tool Returned */}
              <Box display="flex" gap={2} position="relative">
                <Box
                  width={24}
                  height={24}
                  borderRadius="50%"
                  backgroundColor={
                    selectedLog.response.success ? "#4caf50" : "#f44336"
                  }
                  sx={{ border: "2px solid #1e1e1e", zIndex: 1 }}
                />
                <Box flex={1}>
                  <Typography variant="body1" fontWeight="bold">
                    Tool returned with following result
                  </Typography>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="#ccc">
                      Response returned to the client
                    </Typography>
                    <Typography variant="body2" color="#ccc">
                      Overall time: {selectedLog.duration}s
                    </Typography>
                  </Box>
                  <Paper
                    sx={{
                      mt: 1,
                      p: 2,
                      backgroundColor: "#2a2a2a",
                      borderRadius: 2,
                    }}
                  >
                    <Chip
                      label="RESPONSE"
                      size="small"
                      sx={{ mb: 1, backgroundColor: "#444", color: "#ccc" }}
                    />
                    <Box
                      component="pre"
                      sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        backgroundColor: "#111",
                        color: "#eee",
                        p: 2,
                        borderRadius: 1,
                        maxHeight: "40vh",
                        overflowY: "auto",
                      }}
                    >
                      {JSON.stringify(selectedLog.response, null, 2)}
                    </Box>
                  </Paper>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>
    </>
  );
}
